import React from "react";
import { Card, Progress, Icon, Tooltip } from "antd";
import { VisualSeoDimension } from "../types";
import { getColorByScore } from "./utils";

const { Meta } = Card;

const DIMENSION_TITLES: Record<string, string> = {
    above_the_fold_value_clarity: "Above-the-Fold Value Clarity",
    message_intent_alignment: "Message-Intent Alignment",
    attention_flow_and_visual_hierarchy: "Attention Flow & Visual Hierarchy",
    friction_and_cognitive_load: "Friction & Cognitive Load",
    trust_and_credibility: "Trust & Credibility",
    conversion_path_clarity: "Conversion Path Clarity",
}

const DIMENSION_TAGLINES: Record<string, string> = {
    above_the_fold_value_clarity: "How clearly the page communicates its value and purpose to first-time visitors.",
    message_intent_alignment: "How well the page's layout and tone match what visitors are looking for.",
    attention_flow_and_visual_hierarchy: "How well the page's design guides visitors' attention and creates a clear visual hierarchy.",
    friction_and_cognitive_load: "How well the page's design reduces cognitive load and friction for visitors.",
    trust_and_credibility: "How well the page's design builds trust and credibility for the website.",
    conversion_path_clarity: "How well the page's design guides visitors to take action.",
}

const DIMENSION_TOOLTIPS: Record<string, string> = {
    above_the_fold_value_clarity: "This analysis focuses on the very first screen visitors see before they scroll. It checks whether that initial view instantly tells people what the page is about and reassures them they're in the right place, giving them a clear reason to keep scrolling rather than leaving.",
    message_intent_alignment: "This analysis focuses on whether the page's visual style and wording match what visitors came looking for. It checks if the layout and tone fit their intent, whether they want to learn something, buy a product, or quickly find a specific section, so the experience feels right for their purpose.",
    attention_flow_and_visual_hierarchy: "This analysis focuses on where a visitor's eye is naturally drawn as they move down the page. It checks whether the layout guides attention to the right elements in the right order, using size, spacing, and structure to lead people smoothly toward what matters most instead of leaving them unsure where to look.",
    friction_and_cognitive_load: " This analysis focuses on the visual or structural elements that slow visitors down or cause frustration. It checks for things like cluttered layouts, dense walls of text, or confusing menus that make the page feel harder to use, since too much effort or mental strain pushes people to give up and leave.",
    trust_and_credibility: "This analysis focuses on whether the page proves it is legitimate and trustworthy. It checks for signals that build confidence, such as reviews, testimonials, client logos, security badges, or clear company details, which reassure visitors that the site is genuine and worth engaging with.",
    conversion_path_clarity: "This analysis focuses on how well the page guides visitors toward its main goal. It checks whether the next step is obvious and easy to take, whether that means signing up, making a purchase, submitting a form, or continuing to explore more content, so people aren't left wondering what to do next.",
}

type Props = {
    dimension: VisualSeoDimension;
}

export default function DimensionCard({ dimension }: Props) {
    const { key, score, summary, strengths, friction_points } = dimension;

    return (
        <Card
            className="visual-seo-dimension-card"
            title={
                <Meta
                    title={
                        <>
                            {DIMENSION_TITLES[key]}
                            <Tooltip title={DIMENSION_TOOLTIPS[key]}>
                                <Icon className="title-tooltip" type="question-circle" />
                            </Tooltip>
                        </>
                    }
                    description={DIMENSION_TAGLINES[key]}
                />
            }
        >
            <div className="dimension-summary-row">
                <div className="dimension-score">
                    <Progress
                        type="circle"
                        percent={score}
                        width={100}
                        strokeWidth={8}
                        strokeColor={getColorByScore(score)}
                        format={(percent) => `${percent}%`}
                    />
                </div>
                <div className="dimension-summary" dangerouslySetInnerHTML={{ __html: summary }} />
            </div>

            {strengths && strengths.length > 0 && (
                <div className="dimension-strengths">
                    <h4>Strengths</h4>
                    <ul className="dimension-list strengths-list">
                        {strengths.map((strength, index) => (
                            <li key={index}>
                                <Icon type="check-circle" theme="filled" />
                                <span dangerouslySetInnerHTML={{ __html: strength }} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {friction_points && friction_points.length > 0 && (
                <div className="dimension-friction">
                    <h4>Friction Points</h4>
                    <ul className="dimension-list friction-list">
                        {friction_points.map((friction, index) => (
                            <li key={index}>
                                <Icon type="exclamation-circle" theme="filled" />
                                <span dangerouslySetInnerHTML={{ __html: friction }} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Card>
    );
}
